import { useCallback, useRef } from 'react';
import { CancelledError, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LayoutItem } from 'react-grid-layout';
import { useAuth } from '@/lib/auth';
import {
  clearLocalDashboardLayout,
  decideDashboardLayoutFetch,
  readLocalDashboardLayout,
  writeLocalDashboardLayout,
} from '@/lib/dashboardLayoutStorage';
import { supabase } from '@/lib/supabase';
import { parseDashboardLayout, serializeDashboardLayout } from '@/pages/dashboard/widgetCatalog';
import type { Json } from '@/types/database.generated';

export const dashboardLayoutQueryKey = (
  userId: string | null | undefined,
  clinicId: string | null | undefined,
) => ['dashboard-layout', userId ?? null, clinicId ?? null] as const;

interface DashboardLayoutRow {
  id: string;
  layout: unknown;
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error ? String((error as { name: unknown }).name) : '';
  const message = 'message' in error ? String((error as { message: unknown }).message) : '';
  return name === 'AbortError' || /aborted/i.test(message);
}

function rememberServerLayout(userId: string, clinicId: string, layout: unknown) {
  try {
    const parsed = parseDashboardLayout(layout);
    if (parsed.length > 0) writeLocalDashboardLayout(localStorage, userId, clinicId, parsed);
    else clearLocalDashboardLayout(localStorage, userId, clinicId);
  } catch {
    // localStorage not available
  }
}

/**
 * Forside widget grid for the signed-in user in the active clinic.
 *
 * Stored in `user_dashboard_layouts` (one row per user + clinic). A browser
 * copy is adopted only when that row does not exist yet; once a row exists,
 * the server replaces the browser copy. Nulstil writes the restored grid to
 * the same row and drops the browser copy first so a stale local layout
 * cannot be adopted back.
 */
export function useDashboardLayout(clinicId: string | null | undefined) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const writeGen = useRef(0);
  const inflight = useRef<AbortController | null>(null);

  const query = useQuery({
    queryKey: dashboardLayoutQueryKey(userId, clinicId),
    enabled: Boolean(userId && clinicId),
    queryFn: async ({ signal }): Promise<DashboardLayoutRow | null> => {
      if (!userId || !clinicId) return null;
      const genAtStart = writeGen.current;
      const stillCurrent = () => writeGen.current === genAtStart && !signal.aborted;
      const ignoreStale = (): never => {
        throw new CancelledError({ revert: true });
      };
      const { data, error } = await supabase
        .from('user_dashboard_layouts')
        .select('id, layout')
        .eq('user_id', userId)
        .eq('clinic_id', clinicId)
        .abortSignal(signal)
        .maybeSingle();
      if (error) throw error;
      if (!stillCurrent()) ignoreStale();

      let local: LayoutItem[] | null = null;
      try {
        local = readLocalDashboardLayout(localStorage, userId, clinicId);
      } catch {
        local = null;
      }
      const decision = decideDashboardLayoutFetch({
        server: data ? parseDashboardLayout(data.layout) : null,
        local,
      });

      if (decision.use === 'server') {
        if (!stillCurrent()) ignoreStale();
        rememberServerLayout(userId, clinicId, data?.layout);
        return data;
      }
      if (decision.use === 'default') {
        if (!stillCurrent()) ignoreStale();
        try {
          clearLocalDashboardLayout(localStorage, userId, clinicId);
        } catch {
          // localStorage not available
        }
        return null;
      }

      const payload = serializeDashboardLayout(decision.layout) as Json;
      const saved = await supabase
        .from('user_dashboard_layouts')
        .upsert(
          { user_id: userId, clinic_id: clinicId, layout: payload },
          { onConflict: 'user_id,clinic_id' },
        )
        .abortSignal(signal)
        .select('id, layout')
        .single();
      if (saved.error) {
        if (isAbortError(saved.error) || signal.aborted) throw saved.error;
        console.error('adopting dashboard layout failed', saved.error);
        return null;
      }
      if (!stillCurrent()) ignoreStale();
      rememberServerLayout(userId, clinicId, saved.data.layout);
      return saved.data;
    },
    staleTime: 30_000,
  });

  const persist = useCallback(
    async (layout: readonly LayoutItem[], mode: 'save' | 'reset') => {
      if (!userId || !clinicId) throw new Error('No user or clinic');
      inflight.current?.abort();
      const controller = new AbortController();
      inflight.current = controller;
      const gen = ++writeGen.current;
      if (mode === 'reset') {
        try {
          clearLocalDashboardLayout(localStorage, userId, clinicId);
        } catch {
          // localStorage not available
        }
      }
      const payload = serializeDashboardLayout(layout) as Json;
      let data: DashboardLayoutRow | null = null;
      let error: unknown = null;
      try {
        const saved = await supabase
          .from('user_dashboard_layouts')
          .upsert(
            { user_id: userId, clinic_id: clinicId, layout: payload },
            { onConflict: 'user_id,clinic_id' },
          )
          .abortSignal(controller.signal)
          .select('id, layout')
          .single();
        data = saved.data;
        error = saved.error;
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err)) return;
        throw err;
      }
      if (controller.signal.aborted || writeGen.current !== gen) return;
      if (error || !data) throw error ?? new Error('No dashboard layout row');
      rememberServerLayout(userId, clinicId, data.layout);
      qc.setQueryData(dashboardLayoutQueryKey(userId, clinicId), data);
    },
    [clinicId, qc, userId],
  );

  const saveLayout = useCallback(
    (layout: readonly LayoutItem[]) => persist(layout, 'save'),
    [persist],
  );

  const resetLayout = useCallback(
    (layout: readonly LayoutItem[]) => persist(layout, 'reset'),
    [persist],
  );

  return {
    storedLayout: parseDashboardLayout(query.data?.layout),
    hasSavedLayout: Boolean(query.data),
    isLoading: query.isLoading,
    saveLayout,
    resetLayout,
  };
}
